import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { translateMenuTerm } from '@/i18n/translateMenuContent';

import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  countSelectedInGroup,
  getProductChoiceGroups,
  previewChoiceOptionPrice,
} from '@/lib/productChoiceGroups';

function choicePriceBadgeStyle({ premium, branding, accent, priceLabel, isFree, freeLabel }) {
  if (!premium) return undefined;
  if (isFree || priceLabel === freeLabel || priceLabel === 'Gratis') {
    return {
      color: branding.muted_text_color,
      borderColor: branding.card_border_color,
      backgroundColor: 'transparent',
    };
  }
  return {
    color: accent,
    borderColor: `${accent}99`,
    backgroundColor: `${accent}14`,
  };
}

function choiceRowStyle({ premium, branding, accent, isSelected }) {
  if (!premium) return undefined;
  return {
    borderColor: isSelected ? accent : branding.card_border_color,
    backgroundColor: isSelected ? `${accent}12` : branding.background_color,
    color: branding.text_color,
  };
}

export default function CatalogChoiceGroupsSection({
  product,
  choiceSelections,
  onToggleChoice,
  standard,
  premium,
  branding: b,
  accent,
  tc,
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const freeLabel = t('catalog.free');
  const groups = getProductChoiceGroups(product);
  if (!groups.length) return null;

  return (
    <>
      {groups.map((group) => {
        const selected = choiceSelections[group.name] || [];
        const selectedCount = countSelectedInGroup(choiceSelections, group.name);
        const freeIncluded = group.free_included || 0;

        return (
          <div key={group.name} className="space-y-3">
            <div>
              <Label
                className={cn('text-lg font-semibold', standard && tc.productTitle)}
                style={premium ? { color: b.text_color } : undefined}
              >
                {translateMenuTerm(group.name, lng)}
              </Label>
              <p
                className={cn('text-sm mt-1', standard && tc.productMuted)}
                style={premium ? { color: b.muted_text_color } : undefined}
              >
                {freeIncluded > 0
                  ? t('catalog.includedFree', {
                      n: Math.min(selectedCount, freeIncluded),
                      m: freeIncluded,
                    })
                  : t('catalog.chooseOptions')}
                {group.max_selections
                  ? ` · ${t('catalog.maxSelections', { n: group.max_selections })}`
                  : ''}
              </p>
            </div>

            <div className="space-y-2">
              {group.options.map((option) => {
                const selIdx = selected.indexOf(option.name);
                const isSelected = selIdx >= 0;
                const isFreeSelection = isSelected
                  ? selIdx < freeIncluded
                  : previewChoiceOptionPrice(group, option, selectedCount) === 0;
                const priceLabel = isFreeSelection
                  ? freeLabel
                  : `+$${(Number(option.price) || 0).toLocaleString()}`;

                return (
                  <button
                    key={option.name}
                    type="button"
                    className={cn(
                      'w-full flex items-center justify-between p-3 border rounded-lg text-left transition-colors',
                      !premium && isSelected && 'border-primary bg-primary/5',
                      standard && tc.productTitle,
                    )}
                    style={choiceRowStyle({ premium, branding: b, accent, isSelected })}
                    onClick={() => onToggleChoice(group, option.name)}
                  >
                    <span
                      className={cn('flex items-center gap-2 font-medium', standard && tc.productTitle)}
                      style={premium ? { color: b.text_color } : undefined}
                    >
                      {isSelected ? (
                        <Check
                          className="w-4 h-4 shrink-0"
                          style={premium ? { color: isFreeSelection ? b.text_color : accent } : undefined}
                        />
                      ) : null}
                      {translateMenuTerm(option.name, lng)}
                    </span>
                    <Badge
                      variant={isFreeSelection ? 'secondary' : 'outline'}
                      className={cn(!premium && 'shrink-0')}
                      style={choicePriceBadgeStyle({
                        premium,
                        branding: b,
                        accent,
                        priceLabel,
                        isFree: isFreeSelection,
                        freeLabel,
                      })}
                    >
                      {priceLabel}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}
